import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {WidgetCheckboxV0Utils} from './widget-checkbox-v0.utils';
import {WidgetCheckboxV0NodeDto} from './models/widget-checkbox-v0-node.dto';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {TranslateService} from '@ngx-translate/core';
import {WidgetCheckboxV0Mode} from './models/widget-checkbox-v0-mode';
import { from, Observable } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {WidgetCheckboxV0TreeDialogComponent} from "./dialog/widget-checkbox-v0-tree-dialog.component";
import {WidgetCheckboxV0ChildrenDto} from "./models/widget-checkbox-v0-children.dto";

@Component({
  selector: 'pt-widget-checkbox-v0-edit',
  template: `
    <div *ngIf='show'>
      <ng-container [ngSwitch]='mode'>
        <ng-template [ngSwitchCase]='M.CHILDREN'>

          <pt-widget-nodata [noDataLabel]="noDataLabel" *ngIf="childrenData.length === 0"></pt-widget-nodata>

          <pt-widget-checkbox-v0-edit-children
            [checkbox]="checkbox"
            [childrenData]="childrenData"
            (changeValue)="changeValue.emit($event)">
          </pt-widget-checkbox-v0-edit-children>
        </ng-template>

        <ng-template [ngSwitchCase]='M.CHECKBOX'>

          <ng-container *ngIf="numCheckbox < flatViewMaxSize">
            <pt-widget-nodata [noDataLabel]="noDataLabel" *ngIf="children.length === 0" ></pt-widget-nodata>

            <div [ngClass]='[numCheckbox > 5 ? "wcv0" : ""]'>
              <pt-widget-checkbox-v0-form-item
                class='wcv0-item'
                [ngClass]='[numCheckbox <= 5 ? "w-100" : ""]'
                *ngFor='let checkbox of children'
                [checkbox]='checkbox'
                (changeValue)='updateCheckBox(checkbox, $event)'>
              </pt-widget-checkbox-v0-form-item>
            </div>
          </ng-container>

          <ng-container *ngIf="numCheckbox >= flatViewMaxSize">
            <pt-widget-nodata [noDataLabel]="noDataLabel" *ngIf="checkbox.length === 0" ></pt-widget-nodata>

            <pt-widget-checkbox-v0-select
              [checkbox]="checkbox"
              (changeSelectedRowId)="changeSelectedRowId($event)">
            </pt-widget-checkbox-v0-select>
          </ng-container>
        </ng-template>

        <ng-template [ngSwitchCase]='M.TREE'>
          <!--DIALOG-->
        </ng-template>

        <ng-template [ngSwitchCase]='M.SELECT_CATEGORY'>

          <pt-widget-nodata [noDataLabel]="noDataLabel" *ngIf="checkbox.length === 0"></pt-widget-nodata>

          <ng-select bindLabel='label'
                     bindValue='label'
                     appendTo='body'
                     class='ng-select--custom mb-2'
                     [items]='checkbox' [ngModel]='currentGroup'
                     [placeholder]='"PLACEHOLDERS.SELECT" | translate'
                     (ngModelChange)='changeGroupSelected($event)'
          ></ng-select>

          <pt-widget-checkbox-v0-form-item
            *ngFor='let checkbox of children'
            [checkbox]='checkbox'
            (changeValue)='updateCheckBox(checkbox, $event)'>
          </pt-widget-checkbox-v0-form-item>
        </ng-template>

        <ng-template ngSwitchDefault></ng-template>
      </ng-container>
    </div>
  `,
  styles: [`

    .ng-select--custom {
      width: 400px;
    }

    .wcv0 {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      column-gap: 5px;
      row-gap: 10px;
    }

    .wcv0-item {
      width: 100%;
      display: inline-flex;
      padding: 0;
      min-height: 24px;
      margin-bottom: 1px;
    }
  `],
  encapsulation: ViewEncapsulation.None
})
export class WidgetCheckboxV0EditComponent implements OnChanges, OnDestroy {
  @Input() noDataLabel?: string;
  @Input() internalId: number;
  @Input() checkbox: WidgetCheckboxV0NodeDto[];
  @Input() mode: WidgetCheckboxV0Mode;
  @Input() show: boolean;
  @Input() title: string;
  @Input() hasChildren: boolean
  @Input() childrenData: WidgetCheckboxV0ChildrenDto[];
  @Input() flatViewMaxSize = 13;

  @Output() changeValue = new EventEmitter<string[]>();
  @Output() changeAndSaveValue = new EventEmitter<string[]>();
  @Output() closeWithoutSave = new EventEmitter<boolean>();

  numCheckbox = 0;
  // non utilizzo l'uuid perchè i gruppi possono non averlo
  currentGroup = '';
  children: WidgetCheckboxV0NodeDto[] = [];
  M = WidgetCheckboxV0Mode;
  private actualValue: string[];
  private modalRef?: NgbModalRef;

  constructor(private widgetDialogService: WidgetDialogService,
              private modalService: NgbModal,
              private translateService: TranslateService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.actualValue = WidgetCheckboxV0Utils.checkboxNodeToValue(this.checkbox);
    this.numCheckbox = WidgetCheckboxV0Utils.getNumOptions(this.checkbox);
    this.changeValue.emit(this.actualValue);

    // NOTA: il cambiamento dell'input checkbox deve chiamare il metodo setMode che inizializza le variabili
    if (changes.mode || changes.checkbox) {
      this.setMode(this.mode, this.checkbox);
    }

    if (changes.show) {
      this.toggleCheckbox(changes.show.currentValue, this.mode);
    }
  }

  updateCheckBox(checkbox: WidgetCheckboxV0NodeDto, checked: boolean) {
    if (checked) {
      if (!!checkbox.uuid && !this.actualValue.includes(checkbox.uuid)) {
        this.actualValue.push(checkbox.uuid);
      }
    } else {
      this.actualValue = this.actualValue.filter((v) => v !== checkbox.uuid);
    }
    this.changeValue.emit(this.actualValue);
  }

  changeGroupSelected(group: string) {
    this.currentGroup = group;
    const currentGroup = this.checkbox.find((c) => c.label === group);
    this.children = currentGroup ? currentGroup.children : [];
    this.actualValue = [];
    this.changeValue.emit(this.actualValue);
  }

  changeSelectedRowId(selectedNodeIds: string[]) {
    this.changeValue.emit(selectedNodeIds ? selectedNodeIds : []);
  }

  ngOnDestroy(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = undefined;
    }
  }

  private setMode(mode: WidgetCheckboxV0Mode, checkbox: WidgetCheckboxV0NodeDto[]) {
    this.children = [];
    let currentGroup = '';

    switch (mode) {
      case WidgetCheckboxV0Mode.CHECKBOX:
        this.children = WidgetCheckboxV0Utils.getOnlyChildren(checkbox);
        break;

      case WidgetCheckboxV0Mode.SELECT_CATEGORY:
        checkbox.forEach(c => {
          if (c?.children?.find(children => children.checked)) {
            currentGroup = c.label;
          }
        });
        this.changeGroupSelected(currentGroup);
        break;

      default:
    }
  }

  private toggleCheckbox(show: boolean, mode: WidgetCheckboxV0Mode) {
    if (show && mode) {
      switch (mode) {
        case WidgetCheckboxV0Mode.CHECKBOX:
          this.openCheckBox()
          break;
        case WidgetCheckboxV0Mode.SELECT_CATEGORY:
          this.openSelectCategory()
          break;
        case WidgetCheckboxV0Mode.TREE:
          this.openCheckBoxTree()
          break;
      }
    }
  }

  private openCheckBoxTree() {
    this.modalRef = this.modalService.open(WidgetCheckboxV0TreeDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: 'md',
        keyboard: true
      });
    this.modalRef.componentInstance.showShowOnlySelectedFilter = true;
    this.modalRef.componentInstance.title = this.title;

    this.modalRef.componentInstance.loadCheckbox(this.checkbox);
    from(this.modalRef.result).subscribe((success) => {
      if (success) {
        this.changeAndSaveValue.emit(success);
      } else {
        this.closeWithoutSave.emit();
      }
    }, () => {
      this.closeWithoutSave.emit();
    })
  }

  private openSelectCategory() {
  }

  private openCheckBox() {
  }
}


